import { useState, useContext, useEffect, useRef } from "react";
import { useNotification } from "../contexts/NotificationContext";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CheckCircle2,
  Download,
  ChevronRight,
  Star,
  Package,
  Zap,
  Crown,
  ShieldCheck,
  Tag,
  CreditCard,
  XCircle,
  Sparkles
} from "lucide-react";

import { apiService } from "../backend/api";
import { getBaseUrl } from "../config/apiConfig";

import { LoginContext } from "../LoginContext";
import { initiateOneTimePayment } from "../services/stripeService";
import usePlans from "../hooks/usePlans";
import DownloadsPaketeSkeleton from "./skeletons/DownloadsPaketeSkeleton";

const DownloadsPakete = () => {
  const { showNotification } = useNotification();
  const [activeTab, setActiveTab] = useState("downloads");
  const navigate = useNavigate();
  const { loginInfo } = useContext(LoginContext);
  const loggedInUser = loginInfo?.username;
  const { t } = useTranslation();

  const mobileScrollRef = useRef(null);
  const [mobileCardHeight, setMobileCardHeight] = useState(null);
  const [activePromos, setActivePromos] = useState([]);
  const [launchPromos, setLaunchPromos] = useState(null);

  // Use React Query hook
  const { data: plansData, isLoading } = usePlans();
  const dbPlans = plansData?.filter(p => p.type === 'one-time') || [];

  // Fetch active promo codes + launch promotions
  useEffect(() => {
    const fetchPromos = async () => {
      try {
        const data = await apiService.request(`${getBaseUrl()}/api/payment/active-promos`);
        setActivePromos(data);
      } catch {
        setActivePromos([]);
      }
    };
    const fetchLaunchPromos = async () => {
      try {
        const data = await apiService.request(`${getBaseUrl()}/api/payment/launch-promotions`);
        setLaunchPromos(data);
      } catch {
        setLaunchPromos(null);
      }
    };
    fetchPromos();
    fetchLaunchPromos();
  }, []);

  // Dynamisch verfügbare Höhe für Mobile-Karten berechnen
  useEffect(() => {
    const calcHeight = () => {
      if (!mobileScrollRef.current) return;
      const rect = mobileScrollRef.current.getBoundingClientRect();
      const available = window.innerHeight - rect.top - 32;
      setMobileCardHeight(Math.max(available, 280));
    };
    calcHeight();
    window.addEventListener('resize', calcHeight);
    const timer = setTimeout(calcHeight, 250);
    return () => { window.removeEventListener('resize', calcHeight); clearTimeout(timer); };
  }, [dbPlans]);

  if (isLoading) return <DownloadsPaketeSkeleton />;

  const handleOneTimePayment = (paket) => {
    const priceString = paket.totalPrice.replace("€", "").trim();
    const price = Number.parseFloat(priceString.replace(",", "."));

    if (isNaN(price) || price <= 0) {
      console.error('Fehler beim Parsen des Preises:', paket.totalPrice);
      showNotification('Fehler beim Verarbeiten des Preises. Bitte versuchen Sie es erneut.', 'error');
      return;
    }

    initiateOneTimePayment(paket.name, price, loggedInUser);
  };

  const iconMap = {
    Package,
    Zap,
    Star,
    Crown,
    Download
  };

  // Referenzpreis: höchster Pro-Download-Preis aus One-Time-Paketen (= Einzelkauf)
  const basePricePerDl = dbPlans.length > 0
    ? Math.max(...dbPlans.filter(p => p.downloads > 0).map(p => p.price / p.downloads))
    : 0;

  // Farb-Mapping passend zu den Abo-Karten
  const getHeaderBg = (name) => {
    if (name === 'Einzeldownload') return 'bg-slate-800';
    if (name.includes('Starter')) return 'bg-[#1e3a47]';
    if (name.includes('Basic')) return 'bg-[#1e4a5a]';
    if (name.includes('Standard')) return 'bg-[#0f7699]';
    if (name.includes('Professional')) return 'bg-[#0d6b8a]';
    if (name.includes('Enterprise')) return 'bg-[#5c8a9d]';
    return 'bg-slate-800';
  };

  const getArrowColor = (name) => {
    if (name === 'Einzeldownload') return '#1e293b';
    if (name.includes('Starter')) return '#1e3a47';
    if (name.includes('Basic')) return '#1e4a5a';
    if (name.includes('Standard')) return '#0f7699';
    if (name.includes('Professional')) return '#0d6b8a';
    if (name.includes('Enterprise')) return '#5c8a9d';
    return '#1e293b';
  };

  // "(Package)" auf zweite Zeile setzen
  const formatName = (name) => {
    const translated = t(name);
    if (translated.includes('(Package)')) {
      const parts = translated.split('(Package)');
      return <>{parts[0].trim()}<br /><span className="text-[80%] opacity-70">(Package)</span></>;
    }
    return translated;
  };

  const pkgDiscountPercent = launchPromos?.packageDiscount?.percent || 0;

  const pakete = dbPlans.map(plan => {
    const ppd = plan.downloads > 0 ? plan.price / plan.downloads : 0;
    const savingsPercent = ppd > 0 && basePricePerDl > 0 && ppd < basePricePerDl
      ? Math.round((1 - ppd / basePricePerDl) * 100)
      : 0;
    const launchPrice = pkgDiscountPercent > 0 && plan.price > 0
      ? plan.price * (1 - pkgDiscountPercent / 100)
      : null;
    return {
      name: plan.name,
      id: plan.name.toLowerCase().replace(/\s/g, '-'),
      totalPrice: `${plan.price.toFixed(2).replace('.', ',')}€`,
      priceRaw: plan.price,
      launchPrice: launchPrice ? `${launchPrice.toFixed(2).replace('.', ',')}€` : null,
      downloads: plan.downloads,
      priceLabel: ppd > 0 ? `${ppd.toFixed(2).replace('.', ',')}€ / Download` : '',
      savingsLabel: savingsPercent > 0 ? `${savingsPercent}% Rabatt` : '',
      features: plan.features || [],
      missingFeatures: plan.missingFeatures || [],
      highlightFeatures: plan.highlightFeatures || [],
      icon: iconMap[plan.icon] || Package,
      isRecommended: plan.name.includes('Standard'),
      headerBg: getHeaderBg(plan.name),
      arrowColor: getArrowColor(plan.name),
    };
  });

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 relative font-sans">
      {/* Page Header - Full Width */}
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 shrink-0 relative z-10">
        <div className="px-3 sm:px-6 lg:px-8 py-2 sm:py-5">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-14 sm:h-14 bg-[#0f7699]/10 dark:bg-[#4fc3f7]/15 rounded-xl sm:rounded-2xl flex items-center justify-center shrink-0">
              <Tag className="text-[#0f7699] dark:text-[#4fc3f7]" size={22} />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg sm:text-2xl md:text-3xl font-black text-[#0f7699] dark:text-[#4fc3f7] tracking-tight">
                {t("Preise")}
              </h1>
              <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 font-medium mt-0.5 hidden sm:block">
                {t("paketepage.subtitle")}
              </p>
            </div>
          </div>
          {/* Mobile Tab Navigation inside Header */}
          <div className="flex lg:hidden mt-2 bg-slate-100/80 dark:bg-slate-800/80 p-1 rounded-xl">
            <button
              onClick={() => { setActiveTab("subscriptions"); navigate("/pakete"); }}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg font-bold text-sm transition-all duration-200 ${activeTab === "subscriptions" ? 'bg-white dark:bg-slate-700 text-[#0f7699] dark:text-[#4fc3f7] shadow-sm' : 'text-slate-400 dark:text-slate-500'}`}
            >
              <ShieldCheck size={15} />
              <span>Abos</span>
            </button>
            <button
              onClick={() => { setActiveTab("downloads"); navigate("/downloadspakete"); }}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg font-bold text-sm transition-all duration-200 ${activeTab === "downloads" ? 'bg-white dark:bg-slate-700 text-[#0f7699] dark:text-[#4fc3f7] shadow-sm' : 'text-slate-400 dark:text-slate-500'}`}
            >
              <Download size={15} />
              <span>Downloads</span>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Promo Banner */}
      {activePromos.length > 0 && (
        <div className="lg:hidden relative z-10">
          <div className="bg-green-50 dark:bg-green-950/40 border-b border-green-100 dark:border-green-900/50 px-3 py-2.5 flex items-center gap-2.5">
            <Tag size={15} className="text-green-600 dark:text-green-400 shrink-0" />
            <p className="text-xs text-slate-600 dark:text-slate-300 leading-snug flex-1 min-w-0">
              {activePromos.map((promo, i) => (
                <span key={i}>
                  {i > 0 && ' · '}
                  <strong>{promo.code}</strong>{' '}
                  {promo.percent_off ? `−${promo.percent_off}%` : promo.amount_off ? `−${promo.amount_off}€` : ''}
                </span>
              ))}
            </p>
          </div>
        </div>
      )}
      {launchPromos?.packageDiscount && (
        <div className="lg:hidden relative z-10">
          <div className="bg-purple-50 dark:bg-purple-950/40 border-b border-purple-100 dark:border-purple-900/50 px-3 py-2.5 flex items-center gap-2.5">
            <Sparkles size={15} className="text-purple-600 dark:text-purple-400 shrink-0" />
            <p className="text-xs text-slate-600 dark:text-slate-300 leading-snug flex-1 min-w-0">
              <strong>Launch-Aktion:</strong> {launchPromos.packageDiscount.percent}% Rabatt auf alle Download-Pakete — automatisch angewendet!
            </p>
          </div>
        </div>
      )}

      {/* Background Decor */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-gradient-to-b from-[#0f7699]/10 dark:from-[#4fc3f7]/10 to-transparent rounded-full blur-[120px] -z-10" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 lg:py-8 md:py-12 relative z-10">

        {/* Navigation Tabs — Desktop only */}
        <div className="hidden lg:flex justify-center mb-8 md:mb-16">
          <div className="bg-white/50 dark:bg-slate-800/50 backdrop-blur-xl p-1.5 rounded-2xl border border-white dark:border-slate-700 flex shadow-xl shadow-slate-200/50 dark:shadow-slate-900/50">
            <button
              onClick={() => { setActiveTab("subscriptions"); navigate("/pakete"); }}
              className={`flex items-center space-x-2 px-3 sm:px-6 py-2.5 sm:py-3 rounded-xl font-bold transition-all duration-300 ${activeTab === "subscriptions" ? 'bg-[#0f7699] dark:bg-[#4fc3f7] text-white dark:text-slate-900 shadow-lg' : 'text-slate-500 dark:text-slate-400 hover:text-[#0f7699] dark:hover:text-[#4fc3f7] hover:bg-[#0f7699]/5 dark:hover:bg-[#4fc3f7]/10'}`}
            >
              <ShieldCheck size={18} />
              <span className="hidden sm:inline">Abonnements</span>
              <span className="sm:hidden text-sm">Abos</span>
            </button>
            <button
              onClick={() => { setActiveTab("downloads"); navigate("/downloadspakete"); }}
              className={`flex items-center space-x-2 px-3 sm:px-6 py-2.5 sm:py-3 rounded-xl font-bold transition-all duration-300 ${activeTab === "downloads" ? 'bg-[#0f7699] dark:bg-[#4fc3f7] text-white dark:text-slate-900 shadow-lg shadow-sky-900/20' : 'text-slate-500 dark:text-slate-400 hover:text-[#0f7699] dark:hover:text-[#4fc3f7] hover:bg-[#0f7699]/5 dark:hover:bg-[#4fc3f7]/10'}`}
            >
              <Download size={18} />
              <span className="hidden sm:inline">Download-Pakete</span>
              <span className="sm:hidden text-sm">Downloads</span>
            </button>
          </div>
        </div>

        {/* Desktop Promo Banner */}
        {activePromos.length > 0 && (
          <div className="hidden lg:flex max-w-4xl mx-auto mb-8 bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800/30 rounded-2xl sm:rounded-3xl p-4 sm:p-6 items-start space-x-3 sm:space-x-5 animate-in slide-in-from-top-4 duration-500">
            <div className="bg-white dark:bg-slate-800 p-2.5 sm:p-3 rounded-xl sm:rounded-2xl shadow-sm text-green-600 dark:text-green-400">
              <Tag size={24} />
            </div>
            <div className="flex-1 space-y-1 min-w-0">
              <h3 className="font-bold text-slate-800 dark:text-white text-sm sm:text-base">Aktive Rabattcodes</h3>
              <div className="flex flex-wrap gap-2">
                {activePromos.map((promo, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1 bg-white dark:bg-slate-800 border border-green-200 dark:border-green-800/30 rounded-full text-xs font-bold text-green-700 dark:text-green-400">
                    <Tag size={12} />
                    {promo.code} — {promo.percent_off ? `${promo.percent_off}%` : promo.amount_off ? `${promo.amount_off}€` : ''} Rabatt
                  </span>
                ))}
              </div>
              <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-1">Code beim Checkout eingeben</p>
            </div>
          </div>
        )}

        {/* Launch Promotion Banner — Desktop */}
        {launchPromos?.packageDiscount && (
          <div className="hidden lg:flex max-w-4xl mx-auto mb-8 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border border-purple-100 dark:border-purple-800/30 rounded-2xl sm:rounded-3xl p-4 sm:p-6 items-start space-x-3 sm:space-x-5 animate-in slide-in-from-top-4 duration-500">
            <div className="bg-white dark:bg-slate-800 p-2.5 sm:p-3 rounded-xl sm:rounded-2xl shadow-sm text-purple-600 dark:text-purple-400">
              <Sparkles size={24} />
            </div>
            <div className="flex-1 space-y-1 min-w-0">
              <h3 className="font-bold text-slate-800 dark:text-white text-sm sm:text-base">Launch-Aktion: {launchPromos.packageDiscount.percent}% Paket-Rabatt</h3>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                Spare {launchPromos.packageDiscount.percent}% auf alle Download-Pakete — wird automatisch beim Checkout angewendet!
              </p>
              {launchPromos.packageDiscount.endDate && (
                <p className="text-[10px] text-slate-500 dark:text-slate-400">Gültig bis {new Date(launchPromos.packageDiscount.endDate).toLocaleDateString('de-DE')}</p>
              )}
            </div>
          </div>
        )}

        {/* Pricing Grid — Desktop (identisch zu Abo-Design) */}
        <div className="hidden lg:flex items-stretch justify-center gap-0 bg-white dark:bg-slate-800 rounded-[2rem] overflow-hidden shadow-2xl border border-slate-100 dark:border-slate-700 max-w-7xl mx-auto">
          {pakete.map((paket) => (
            <div
              key={paket.name}
              className={`flex-1 flex flex-col border-r border-slate-100 dark:border-slate-700 last:border-r-0 transition-all duration-300 hover:z-30 hover:shadow-[0_0_50px_rgba(0,0,0,0.1)] dark:hover:shadow-[0_0_50px_rgba(0,0,0,0.3)] relative ${paket.isRecommended ? 'bg-slate-50/30 dark:bg-slate-700/30' : 'bg-white dark:bg-slate-800'}`}
            >
              <div className={`${paket.headerBg} p-6 text-center text-white relative`}>
                <h3 className="text-xs font-black uppercase tracking-[0.15em] mb-4 opacity-90">{formatName(paket.name)}</h3>
                <div className="space-y-1 mb-2">
                  {paket.launchPrice ? (
                    <>
                      <span className="text-lg font-light tracking-tight line-through opacity-50">{paket.totalPrice.replace(',00', '')}</span>
                      <br />
                      <span className="text-3xl xl:text-4xl font-extralight tracking-tight">{paket.launchPrice.replace(',00', '')}</span>
                      <div className="text-[9px] font-black uppercase tracking-widest opacity-60">-{pkgDiscountPercent}% Launch-Rabatt</div>
                    </>
                  ) : (
                    <>
                      <span className="text-3xl xl:text-4xl font-extralight tracking-tight">{paket.totalPrice.replace(',00', '')}</span>
                      <div className="text-[9px] font-black uppercase tracking-widest opacity-60">Einmalig</div>
                    </>
                  )}
                </div>
                <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[12px] border-l-transparent border-r-[12px] border-r-transparent border-t-[12px]" style={{ borderTopColor: paket.arrowColor }}></div>
              </div>

              <div className="flex-1 flex flex-col p-5 bg-white dark:bg-slate-800">
                <div className="flex flex-col items-center space-y-4 mb-8">
                  <div className="text-center">
                    <div className="text-lg font-black text-slate-800 dark:text-white">{paket.downloads}</div>
                    <div className="text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">{t("downloadspakete.downloads")}</div>
                    {paket.priceLabel && (
                      <div className="text-xs font-semibold text-[#0f7699] dark:text-[#4fc3f7] mt-2">{paket.priceLabel}</div>
                    )}
                  </div>
                  {paket.savingsLabel && (
                    <div className="flex items-center justify-center">
                      <span className="text-[10px] font-black text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-2.5 py-0.5 rounded-full border border-green-100 dark:border-green-800/30">{paket.savingsLabel}</span>
                    </div>
                  )}
                  <div className="w-full h-px bg-slate-50 dark:bg-slate-700" />
                  <div className="w-full space-y-3">
                    {paket.highlightFeatures.map((item, i) => (
                      <div key={`highlight-${i}`} className="flex flex-col items-center justify-center py-3 border-b border-amber-100 dark:border-amber-800/30 last:border-0 group bg-amber-50/50 dark:bg-amber-900/10 rounded-xl">
                        <Sparkles size={16} className="text-amber-500 dark:text-amber-400 mb-1.5 group-hover:scale-110 transition-transform" />
                        <span className="text-[11px] font-bold text-amber-700 dark:text-amber-400 text-center leading-snug">{item}</span>
                      </div>
                    ))}
                    {paket.features.map((item, i) => (
                      <div key={i} className="flex flex-col items-center justify-center py-3 border-b border-slate-50 dark:border-slate-700 last:border-0 group">
                        <CheckCircle2 size={16} className="text-green-500 dark:text-green-400 mb-1.5 group-hover:scale-110 transition-transform" />
                        <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400 text-center leading-snug">{item}</span>
                      </div>
                    ))}
                    {paket.missingFeatures.map((item, i) => (
                      <div key={`missing-${i}`} className="flex flex-col items-center justify-center py-3 border-b border-slate-50 dark:border-slate-700 last:border-0 group">
                        <XCircle size={16} className="text-red-300 dark:text-red-800 mb-1.5" />
                        <span className="text-[11px] font-medium text-slate-300 dark:text-slate-600 text-center leading-snug line-through">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mt-auto pt-6">
                  <button
                    onClick={() => handleOneTimePayment(paket)}
                    className={`w-full py-3.5 rounded-xl font-black text-[10px] uppercase tracking-[0.15em] transition-all active:scale-[0.98] flex items-center justify-center space-x-2 ${
                      paket.isRecommended
                        ? 'bg-[#0f7699] dark:bg-[#4fc3f7] text-white dark:text-slate-900 shadow-lg shadow-sky-900/20 hover:bg-[#0d6b8a] dark:hover:bg-[#81d4fa]'
                        : 'bg-slate-800 dark:bg-slate-600 text-white hover:bg-slate-900 dark:hover:bg-slate-500'
                    }`}
                  >
                    <CreditCard size={16} />
                    <span>{t("downloadspakete.buyNow")}</span>
                  </button>
                  <div className="flex justify-center mt-3">
                    <span className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">Sofort startklar</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Pricing Cards — Mobile Swipe */}
        {(() => {
          const compact = mobileCardHeight && mobileCardHeight < 480;
          return (
            <div className="lg:hidden">
              <div ref={mobileScrollRef} className="flex overflow-x-auto snap-x snap-mandatory gap-4 pb-2 -mx-4 px-4 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}>
                {pakete.map((paket) => (
                  <div
                    key={paket.name}
                    className={`w-[80vw] max-w-[320px] shrink-0 snap-center flex flex-col rounded-2xl overflow-hidden shadow-xl border border-slate-100 dark:border-slate-800 ${paket.isRecommended ? 'bg-slate-50/30 dark:bg-slate-800/30' : 'bg-white dark:bg-slate-900'}`}
                    style={mobileCardHeight ? { height: mobileCardHeight } : {}}
                  >
                    <div className={`${paket.headerBg} ${compact ? 'p-3' : 'p-5'} text-center text-white relative shrink-0`}>
                      <h3 className={`${compact ? 'text-[10px] mb-1' : 'text-xs mb-3'} font-black uppercase tracking-[0.2em] opacity-90`}>{formatName(paket.name)}</h3>
                      <div className={compact ? '' : 'space-y-1'}>
                        {paket.launchPrice ? (
                          <>
                            <span className={`${compact ? 'text-sm' : 'text-base'} font-light tracking-tight line-through opacity-50`}>{paket.totalPrice.replace(',00', '')}</span>
                            <br />
                            <span className={`${compact ? 'text-2xl' : 'text-3xl'} font-extralight tracking-tight`}>{paket.launchPrice.replace(',00', '')}</span>
                            <div className={`${compact ? 'text-[8px]' : 'text-[9px]'} font-black uppercase tracking-widest opacity-60`}>-{pkgDiscountPercent}%</div>
                          </>
                        ) : (
                          <>
                            <span className={`${compact ? 'text-2xl' : 'text-3xl'} font-extralight tracking-tight`}>{paket.totalPrice.replace(',00', '')}</span>
                            <div className={`${compact ? 'text-[8px]' : 'text-[9px]'} font-black uppercase tracking-widest opacity-60`}>Einmalig</div>
                          </>
                        )}
                      </div>
                      <div className={`absolute ${compact ? '-bottom-2' : '-bottom-3'} left-1/2 -translate-x-1/2 w-0 h-0 ${compact ? 'border-l-[7px] border-r-[7px] border-t-[7px]' : 'border-l-[10px] border-r-[10px] border-t-[10px]'} border-l-transparent border-r-transparent`} style={{ borderTopColor: paket.arrowColor }}></div>
                    </div>

                    <div className={`flex-1 flex flex-col ${compact ? 'p-3' : 'p-4'} bg-white dark:bg-slate-900 overflow-y-auto min-h-0`}>
                      <div className={`flex flex-col items-center ${compact ? 'space-y-2 mb-3' : 'space-y-3 mb-5'}`}>
                        <div className="text-center">
                          <div className={`${compact ? 'text-base' : 'text-lg'} font-black text-slate-800 dark:text-white`}>{paket.downloads}</div>
                          <div className={`${compact ? 'text-[8px]' : 'text-[9px]'} font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest`}>{t("downloadspakete.downloads")}</div>
                          {!compact && paket.priceLabel && (
                            <div className="text-[11px] font-semibold text-[#0f7699] dark:text-[#4fc3f7] mt-1">{paket.priceLabel}</div>
                          )}
                        </div>
                        {!compact && paket.savingsLabel && (
                          <div className="flex items-center justify-center">
                            <span className="text-[10px] font-black text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-2.5 py-0.5 rounded-full border border-green-100 dark:border-green-800/30">{paket.savingsLabel}</span>
                          </div>
                        )}
                        <div className="w-full h-px bg-slate-100 dark:bg-slate-800" />
                        <div className={`w-full ${compact ? 'space-y-0.5' : 'space-y-1'}`}>
                          {paket.highlightFeatures.map((item, i) => (
                            <div key={`highlight-${i}`} className={`flex items-center gap-2 ${compact ? 'py-1.5' : 'py-2'} border-b border-amber-100 dark:border-amber-800/30 last:border-0 bg-amber-50/50 dark:bg-amber-900/10 rounded-lg px-2`}>
                              <Sparkles size={compact ? 14 : 16} className="text-amber-500 dark:text-amber-400 shrink-0" />
                              <span className={`${compact ? 'text-[10px]' : 'text-[11px]'} font-bold text-amber-700 dark:text-amber-400 leading-snug`}>{item}</span>
                            </div>
                          ))}
                          {paket.features.map((item, i) => (
                            <div key={i} className={`flex items-center gap-2 ${compact ? 'py-1.5' : 'py-2'} border-b border-slate-50 dark:border-slate-800 last:border-0`}>
                              <CheckCircle2 size={compact ? 14 : 16} className="text-green-500 dark:text-green-400 shrink-0" />
                              <span className={`${compact ? 'text-[10px]' : 'text-[11px]'} font-medium text-slate-500 dark:text-slate-400 leading-snug`}>{item}</span>
                            </div>
                          ))}
                          {paket.missingFeatures.map((item, i) => (
                            <div key={`missing-${i}`} className={`flex items-center gap-2 ${compact ? 'py-1.5' : 'py-2'} border-b border-slate-50 dark:border-slate-800 last:border-0`}>
                              <XCircle size={compact ? 14 : 16} className="text-red-300 dark:text-red-800 shrink-0" />
                              <span className={`${compact ? 'text-[10px]' : 'text-[11px]'} font-medium text-slate-300 dark:text-slate-600 leading-snug line-through`}>{item}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className={`mt-auto ${compact ? 'pt-2' : 'pt-4'}`}>
                        <button
                          onClick={() => handleOneTimePayment(paket)}
                          className={`w-full ${compact ? 'py-3 text-[10px]' : 'py-3.5 text-[10px]'} rounded-xl font-black uppercase tracking-[0.15em] transition-all active:scale-[0.98] flex items-center justify-center space-x-2 ${
                            paket.isRecommended
                              ? 'bg-[#0f7699] dark:bg-[#4fc3f7] text-white dark:text-slate-900 shadow-lg shadow-sky-900/20'
                              : 'bg-slate-800 dark:bg-slate-700 text-white'
                          }`}
                        >
                          <CreditCard size={15} />
                          <span>{t("downloadspakete.buyNow")}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {/* Scroll Indicator Dots */}
              <div className="flex justify-center gap-1.5 py-2">
                {pakete.map((_, index) => (
                  <div key={index} className="w-1.5 h-1.5 rounded-full bg-slate-300 dark:bg-slate-600" />
                ))}
              </div>
            </div>
          );
        })()}

        {/* Global Action Footer — Desktop only */}
        <div className="hidden lg:flex mt-10 sm:mt-20 flex-col md:flex-row items-center justify-center gap-4 sm:gap-6">
          <button
            onClick={() => navigate("/upload")}
            className="flex items-center space-x-3 px-8 py-4 bg-white dark:bg-slate-800 border-2 border-slate-100 dark:border-slate-700 rounded-2xl font-black text-[#0f7699] dark:text-[#4fc3f7] hover:bg-slate-50 dark:hover:bg-slate-700 hover:border-[#0f7699]/20 dark:hover:border-[#4fc3f7]/30 transition-all shadow-xl shadow-slate-200/50 dark:shadow-slate-900/50"
          >
            <span>{t("buttons.backToUpload")}</span>
            <ChevronRight size={20} />
          </button>
        </div>

      </div>
    </div>
  );
};

export default DownloadsPakete;
